import React, { useEffect, useMemo, useRef } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Typography } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import {
  mapResourcesToOptions,
  mapResourceTypesToOptions,
  type Resource,
  type ResourceType,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { toMinute } from "@utils/date-time";
import type { TFunction } from "i18next";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { Resource as ResourceInterface } from "../CloseMovementService";

interface AddResourceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (resource: ResourceInterface) => void;
  resourceTypes: ResourceType[];
  resources: Resource[];
  validateStartAfterAts?: boolean;
  ats?: string | Date | null;
  onlyResourceFieldsRequired?: boolean;
}

type ResourceFormData = {
  resourceType: string;
  resourceName?: string;
  numberOfResources: string;
  startTimeOfUsage?: Date | null;
  endTimeOfUsage?: Date | null;
  connectionTime?: Date | null;
  location?: string;
};

const getResourceSchema = (
  t: TFunction,
  ats?: Date | null,
  validateStartAfterAts?: boolean,
  onlyResourceFieldsRequired = false,
) =>
  z
    .object({
      resourceType: z.string().min(
        1,
        t("form-validation.required", {
          label: t("vessel-call.movement-services.close-form.fields.resource-type"),
        }),
      ),
      resourceName:
        validateStartAfterAts || onlyResourceFieldsRequired
          ? z.string().optional()
          : z.string().min(
              1,
              t("form-validation.required", {
                label: t("vessel-call.movement-services.close-form.fields.resource-name"),
              }),
            ),
      numberOfResources: z
        .string()
        .min(
          1,
          t("form-validation.required", {
            label: t("vessel-call.movement-services.close-form.fields.number-of-resources"),
          }),
        )
        .superRefine((val, ctx) => {
          if (!val || val.trim() === "") {
            return;
          }

          const label = t("vessel-call.movement-services.close-form.fields.number-of-resources");
          const trimmedVal = val.trim();

          if (trimmedVal.includes(".") || trimmedVal.includes(",")) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be an integer (no decimals).`,
            });
            return;
          }

          const num = parseInt(trimmedVal, 10);
          const isInteger = !isNaN(num) && num.toString() === trimmedVal;

          if (!isInteger) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be an integer.`,
            });
            return;
          }

          if (num < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be a positive number.`,
            });
            return;
          }

          if (trimmedVal.length > 4) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("form-validation.must-have-maximum-digits", {
                label,
                max: 4,
              }),
            });
          }
        }),
      startTimeOfUsage: onlyResourceFieldsRequired
        ? z.date().nullable().optional()
        : z
            .date()
            .nullable()
            .refine((val) => val !== null, {
              message: t("form-validation.required", {
                label: t("vessel-call.movement-services.close-form.fields.start-time"),
              }),
            }),
      endTimeOfUsage: onlyResourceFieldsRequired
        ? z.date().nullable().optional()
        : z
            .date()
            .nullable()
            .refine((val) => val !== null, {
              message: t("form-validation.required", {
                label: t("vessel-call.movement-services.close-form.fields.end-time"),
              }),
            }),
      connectionTime: z.date().nullable().optional(),
      location: z.string().optional(),
    })
    .refine(
      (data) => {
        if (!data.endTimeOfUsage || !data.startTimeOfUsage) return true;
        return data.endTimeOfUsage > data.startTimeOfUsage;
      },
      {
        message: t("form-validation.end-time-after-start"),
        path: ["endTimeOfUsage"],
      },
    )
    .refine(
      (data) => {
        if (!ats || !data.startTimeOfUsage) return true;
        const startTime = toMinute(data.startTimeOfUsage);
        return !!startTime && startTime >= ats;
      },
      {
        message: t("form-validation.start-time-after-ats"),
        path: ["startTimeOfUsage"],
      },
    );

const AddResourceDrawer: React.FC<AddResourceDrawerProps> = ({
  isOpen,
  onClose,
  onAdd,
  resourceTypes,
  resources,
  validateStartAfterAts,
  ats,
  onlyResourceFieldsRequired,
}) => {
  const { t } = useTranslation();
  const atsDate = toMinute(ats);

  const resourceTypeOptions = useMemo(
    () => mapResourceTypesToOptions(resourceTypes),
    [resourceTypes],
  );

  const form = useForm<ResourceFormData>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(
      getResourceSchema(t, atsDate, validateStartAfterAts, onlyResourceFieldsRequired),
    ),
    shouldUnregister: true,
    defaultValues: {
      resourceType: "",
      resourceName: "",
      numberOfResources: "",
      startTimeOfUsage: null,
      endTimeOfUsage: null,
      connectionTime: null,
      location: "",
    },
  });

  const resourceType = useWatch({ control: form.control, name: "resourceType" });
  const numberOfResources = useWatch({ control: form.control, name: "numberOfResources" });
  const startTimeOfUsage = useWatch({ control: form.control, name: "startTimeOfUsage" });
  const previousResourceTypeRef = useRef<string>("");
  const minStartTime = useMemo(() => {
    if (!atsDate) {
      return undefined;
    }

    return atsDate;
  }, [atsDate]);
  const minEndTime = useMemo(() => {
    if (!startTimeOfUsage) {
      return undefined;
    }
    return new Date(startTimeOfUsage.getTime() + 60 * 1000);
  }, [startTimeOfUsage]);

  // Get the selected resource type option for display and TUG check
  const selectedResourceTypeOption = useMemo(() => {
    return resourceTypeOptions.find((opt) => opt.value === resourceType) || null;
  }, [resourceTypeOptions, resourceType]);

  // Filter resources based on selected resource type
  const resourceNameOptions = useMemo(() => {
    if (!resourceType || !resources || resources.length === 0) {
      return [];
    }
    const filteredResources = resources.filter(
      (resource) => resource.resourceTypeCode === resourceType,
    );
    return mapResourcesToOptions(filteredResources);
  }, [resources, resourceType]);

  // Show additional fields for TUG
  const isTug =
    selectedResourceTypeOption?.value === "TUG" ||
    selectedResourceTypeOption?.label?.toUpperCase().includes("TUG");

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onValid = (data: ResourceFormData) => {
    const resource: ResourceInterface = {
      additionalResourceType: data.resourceType,
      numberOfResources: parseInt(data.numberOfResources, 10),
      ...(data.startTimeOfUsage && { startTimeOfUsage: data.startTimeOfUsage.toISOString() }),
      ...(data.endTimeOfUsage && { endTimeOfUsage: data.endTimeOfUsage.toISOString() }),
    };

    // Explicitly handle resourceName
    if (data.resourceName && data.resourceName.trim()) {
      resource.resourceName = data.resourceName.trim();
    }

    if (isTug && data.connectionTime) {
      resource.connectionTime = data.connectionTime.toISOString();
    }
    if (isTug && data.location) {
      resource.location = data.location;
    }

    onAdd(resource);
    form.reset();
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  // Reset resourceName when resourceType changes
  useEffect(() => {
    if (resourceType) {
      form.setValue("resourceName", "");
    }
  }, [resourceType, form]);

  useEffect(() => {
    const previousResourceType = previousResourceTypeRef.current;
    const hadResourceType = previousResourceType.trim().length > 0;
    const hasResourceType = (resourceType || "").trim().length > 0;
    const hasResourceCount = (numberOfResources || "").trim().length > 0;

    if (!hadResourceType && hasResourceType && !hasResourceCount) {
      form.setValue("numberOfResources", "1", { shouldDirty: true, shouldValidate: true });
    } else if (hadResourceType && !hasResourceType && hasResourceCount) {
      form.setValue("numberOfResources", "", { shouldDirty: true, shouldValidate: true });
    }

    previousResourceTypeRef.current = resourceType || "";
  }, [resourceType, numberOfResources, form]);

  return (
    <FormProvider {...form}>
      <SideDrawer
        isOpen={isOpen}
        isChildDrawer
        title={
          <div className="flex items-center gap-2">
            <ArrowBackIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("vessel-call.movement-services.close-form.add-resource-title")}
            </Typography>
          </div>
        }
        onClose={handleClose}
        submitLabel={t("vessel-call.movement-services.close-form.buttons.add-resource")}
        onSubmit={onSubmit}
      >
        <Box
          className="w-[600px] px-6 py-4 flex flex-col gap-4"
          sx={{
            "& > .grid > .MuiFormControl-root": {
              gridTemplateRows: "auto auto 1fr",
              alignContent: "start",
            },
          }}
        >
          {/* Resources */}
          <Box
            className="grid grid-cols-2 gap-4"
            sx={{ "& > :last-child": { gridColumnStart: validateStartAfterAts ? 2 : 1 } }}
          >
            <FormItem
              name="resourceType"
              type="autocomplete"
              label={t("vessel-call.movement-services.close-form.fields.resource-type")}
              testId="resource-type-autocomplete"
              options={resourceTypeOptions}
              autocompleteValue={selectedResourceTypeOption}
            />

            {!validateStartAfterAts && (
              <FormItem
                name="resourceName"
                type="autocomplete"
                label={t("vessel-call.movement-services.close-form.fields.resource-name")}
                testId="resource-name-autocomplete"
                options={resourceNameOptions}
                autocompleteValue={
                  resourceNameOptions.find((opt) => opt.value === form.watch("resourceName")) ||
                  null
                }
              />
            )}
            <FormItem
              name="numberOfResources"
              type="number"
              label={t("vessel-call.movement-services.close-form.fields.number-of-resources")}
              testId="number-of-resources-input"
              decimals={0}
              maxDigits={4}
            />
          </Box>

          {/* Start and End time */}
          <div className="grid grid-cols-2 gap-4">
            <FormItem
              name="startTimeOfUsage"
              type="date-picker"
              label={t("vessel-call.movement-services.close-form.fields.start-time")}
              testId="start-time-input"
              timePicker
              combinedInput
              minDate={minStartTime}
            />

            <FormItem
              name="endTimeOfUsage"
              type="date-picker"
              label={t("vessel-call.movement-services.close-form.fields.end-time")}
              testId="end-time-input"
              timePicker
              combinedInput
              minDate={minEndTime ?? undefined}
            />
          </div>

          {/* Additional fields for Tug */}
          {isTug && !validateStartAfterAts && (
            <div className="grid grid-cols-2 gap-4">
              <FormItem
                name="connectionTime"
                type="date-picker"
                label={t("vessel-call.movement-services.close-form.fields.connection-time")}
                testId="connection-time-input"
                timePicker
                combinedInput
              />

              <FormItem
                name="location"
                type="text"
                label={t("vessel-call.movement-services.close-form.fields.location")}
                testId="location-input"
              />
            </div>
          )}
        </Box>
      </SideDrawer>
    </FormProvider>
  );
};

export default AddResourceDrawer;
